import React, { useState, ChangeEvent } from 'react';
import * as XLSX from 'xlsx';
import { exampleHeaders } from '../../../../constants/exampleHeader';
import { toast } from 'react-toastify';
import { errorMessages } from '../../../../constants/messages/errorMessages';

interface UploadCustomerExcelProps {
  onDataUpload: (data: any[]) => void;
  loading: (data: boolean) => void;
}

const UploadCustomerExcel = React.forwardRef((props: UploadCustomerExcelProps) => {
  const { onDataUpload, loading } = props;

  const [typeError, setTypeError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [headerError, setHeaderError] = useState<string | null>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      loading(true);
      const fileTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      const file = e.target.files?.[0];

      if (file) {
        if (fileTypes.includes(file.type)) {
          setTypeError(null);
          setSelectedFile(file);

          const reader = new FileReader();
          reader.readAsArrayBuffer(file);

          reader.onload = (e) => {
            try {
              if (e.target && e.target.result) {
                const excelFile = e.target.result as ArrayBuffer;
                const workbook = XLSX.read(excelFile, { type: 'buffer' });
                const worksheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[worksheetName];
                const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // 🛡️ Zararlı içerik kontrolü
                const hasDangerousKeys = data.some((row: any) =>
                  row.some((cell: any) =>
                    typeof cell === 'string' &&
                    /(__proto__|constructor|prototype)/i.test(cell)
                  )
                );

                if (hasDangerousKeys) {
                  toast.error("Geçersiz içerik: Güvenli olmayan veri tespit edildi.");
                  onDataUpload([]);
                  return;
                }

                const headers: any = data[0];
                const isHeaderValid = exampleHeaders.every(
                  (header, index) => header.label === headers[index]
                );

                if (isHeaderValid) {
                  setHeaderError(null);
                  const transformedData = transformData(data);
                  onDataUpload(transformedData);
                } else {
                  setHeaderError('Başlıklar uygun değil. Lütfen örnek excel dosyasındaki başlıklara uygun bir dosya yükleyin.');
                  onDataUpload([]);
                }
              }
            } catch (err) {
              toast.error("Dosya işleme hatası: " + err);
              onDataUpload([]);
            }
          };
        } else {
          setTypeError('Lütfen yalnızca excel dosya türlerini seçin');
          onDataUpload([]);
        }
      }
    } catch (error) {
      toast.error(errorMessages.anError + " : " + error);
    } finally {
      setTimeout(() => loading(false), 2000);
    }
  };

  // ✅ Object.create(null) kullanarak prototype-free obje üretimi
  const transformData = (data: any[]) => {
    const headersMap = exampleHeaders.reduce((map, header) => {
      map[header.label] = header.id;
      return map;
    }, {} as { [key: string]: string });

    return data.slice(1).reduce((result: any[], row: any) => {
      const rowData = Object.create(null); // prototype pollution koruması

      row.forEach((value: any, index: number) => {
        const header = data[0][index];
        const id = headersMap[header];
        if (id) {
          rowData[id] = value;
        }
      });

      // boş satırları atla
      if (Object.values(rowData).some(value => value !== undefined && value !== '')) {
        result.push(rowData);
      }

      return result;
    }, []);
  };

  const removeFile = () => {
    setSelectedFile(null);
    onDataUpload([]);
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col">
        {!selectedFile ? (
          <input
            type="file"
            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-sky-600 hover:file:bg-violet-100 focus:outline-none"
            required
            onChange={handleFile}
          />
        ) : (
          <div className="flex items-center space-x-4">
            <button
              onClick={removeFile}
              className="text-sm text-slate-500 py-2 px-4 rounded-full border-0 font-semibold bg-violet-50 text-sky-600 hover:bg-violet-100 focus:outline-none"
            >
              <img src="/icons/remove.svg" alt="removeFile" />
            </button>
            <div className="text-sm text-slate-500">{selectedFile.name}</div>
          </div>
        )}
        {typeError && <div className="alert alert-danger text-red-500 px-1">{typeError}</div>}
        {headerError && <div className="alert alert-danger text-red-500 px-1">{headerError}</div>}
      </div>
    </div>
  );
});

export default UploadCustomerExcel;
